import type { EmailTemplateModel } from '@/typings/api/emailCenter';
import { isAutomationMail, isCampaignMail } from '@/utils/emailCenter';
import { F } from '@/utils/intl';
import { Alert, Button, Popconfirm, Space, Tooltip } from 'antd';
import React from 'react';
import AutomationConditions from '../../components/AutomationConditions';
import styles from './TemplateDrawer.less';

export interface TemplateDrawerNoticesProps {
  record: EmailTemplateModel | null;
  isEdit: boolean;
  readOnly: boolean;
  /** An automation owns what this mail says and when — view-only, with the reason. */
  isAutomationTemplate: boolean;
  /** A master owned by another account: read-only until detached. */
  isForeignMaster: boolean;
  canCreate: boolean;
  canPublish: boolean;
  detaching: boolean;
  onDetach: () => void;
  draftBusy: boolean;
  onDraftAction: (action: 'publish' | 'discard') => void;
  /** A save failure with nowhere more specific to land. */
  generalError: string | null;
}

/**
 * Everything the drawer has to say before the editor: why this template cannot
 * be edited, that an unpublished draft is what is on screen, and a save that
 * failed with nowhere more specific to land.
 *
 * Grouped as one component because they share a slot and a rule — each is a
 * banner above the form, and at most a couple are ever up at once.
 */
const TemplateDrawerNotices: React.FC<TemplateDrawerNoticesProps> = ({
  record,
  isEdit,
  readOnly,
  isAutomationTemplate,
  isForeignMaster,
  canCreate,
  canPublish,
  detaching,
  onDetach,
  draftBusy,
  onDraftAction,
  generalError,
}) => {
  // Both providers put such a mail's timing somewhere this page cannot edit — a
  // Mailcoach step's conditions, an NL2Go trigger's filter. See the action below.
  const detachBlocked =
    !!record && (isAutomationMail(record) || isCampaignMail(record));

  return (
    <>
      {/* What the automation does with this mail, so "view-only" comes with
          the reason and the timing rather than just a locked form. */}
      {isAutomationTemplate && (
        <Alert
          className={styles.generalError}
          type="info"
          showIcon
          message={
            <F
              id="pages.emailCenter.templates.automationNotice"
              defaultMessage="An automation sends this email, so it can be viewed here but not edited. Change its content or timing in the automation itself."
            />
          }
          description={
            <div className={styles.automationUsage}>
              {(record?.automations ?? []).map((usage, index) => (
                // Steps have no id on this payload; the position is stable.
                <div className={styles.automationUsageStep} key={index}>
                  <span>
                    {usage.wait_in_days > 0 ? (
                      <F
                        id="pages.emailCenter.templates.automationUsage"
                        defaultMessage="“{name}” on {journey} · sent after {days, plural, one {# day} other {# days}}"
                        values={{
                          name: usage.automation_name ?? '',
                          journey: usage.journey_name,
                          days: usage.wait_in_days,
                        }}
                      />
                    ) : (
                      <F
                        id="pages.emailCenter.templates.automationUsageImmediate"
                        defaultMessage="“{name}” on {journey} · sent immediately"
                        values={{
                          name: usage.automation_name ?? '',
                          journey: usage.journey_name,
                        }}
                      />
                    )}
                  </span>
                  <AutomationConditions conditions={usage.conditions} />
                </div>
              ))}
            </div>
          }
        />
      )}
      {isForeignMaster && (
        <Alert
          className={styles.generalError}
          type="info"
          showIcon
          message={
            <F
              id="pages.emailCenter.templates.masterNotice"
              defaultMessage="This email is a master template managed centrally. Detach it to get your own editable copy. "
            />
          }
          /*
           * Offered for an NL2Go master too — detaching is the only route to an
           * editable copy. Disabled rather than hidden when a step or a campaign
           * trigger sends the mail: repointing those means editing sending
           * conditions the Email Center has no UI for yet.
           */
          action={
            canCreate &&
            (detachBlocked ? (
              <Tooltip
                title={
                  <F
                    id="pages.emailCenter.templates.detachBlocked"
                    defaultMessage="This email is sent by an automation or a campaign, whose sending conditions cannot be changed here yet — so a copy of it could not be put in its place. Detaching will be possible once they can be edited."
                  />
                }
              >
                {/* The span is what still receives the hover: a disabled antd
                  button stops firing pointer events on itself. */}
                <span>
                  <Button size="small" type="primary" disabled>
                    <F
                      id="pages.emailCenter.templates.detachAction"
                      defaultMessage="Detach from master"
                    />
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Popconfirm
                title={
                  <F
                    id="pages.emailCenter.templates.detachConfirm"
                    defaultMessage="Create your own copy? This email stops receiving central updates."
                  />
                }
                onConfirm={onDetach}
              >
                <Button size="small" type="primary" loading={detaching}>
                  <F
                    id="pages.emailCenter.templates.detachAction"
                    defaultMessage="Detach from master"
                  />
                </Button>
              </Popconfirm>
            ))
          }
        />
      )}
      {isEdit && record?.has_draft && !readOnly && (
        <Alert
          className={styles.generalError}
          type="warning"
          showIcon
          message={
            <F
              id="pages.emailCenter.templates.draftPendingNotice"
              defaultMessage="You are editing an unpublished draft. The published version is still the one being sent until you publish."
            />
          }
          action={
            <Space size={4}>
              {canPublish && (
                <Button
                  size="small"
                  type="primary"
                  loading={draftBusy}
                  onClick={() => onDraftAction('publish')}
                >
                  <F
                    id="pages.emailCenter.templates.publishDraft"
                    defaultMessage="Publish draft"
                  />
                </Button>
              )}
              <Popconfirm
                title={
                  <F
                    id="pages.emailCenter.templates.discardDraftConfirm"
                    defaultMessage="Discard the draft? The unpublished changes are lost."
                  />
                }
                onConfirm={() => onDraftAction('discard')}
              >
                <Button size="small" type="text" loading={draftBusy}>
                  <F
                    id="pages.emailCenter.templates.discardDraft"
                    defaultMessage="Discard"
                  />
                </Button>
              </Popconfirm>
            </Space>
          }
        />
      )}
      {generalError && (
        <Alert
          className={styles.generalError}
          type="error"
          showIcon
          message={generalError}
        />
      )}
    </>
  );
};

export default TemplateDrawerNotices;
